import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import Logger from '@medflex/utils/Logger';
// import { StateStore } from 'oidc-client';
import GenericUserManager, { userManagerTypes, oidcClientIds } from './GenericUserManager';

class FlutterStateStore /* implements StateStore */ {
  constructor({ type }) {
    this.type = type;
  }

  async set(key, value) {
    await window.top.flutter_inappwebview.callHandler(
      'authentication_store_set',
      `${this.type}_${key}`,
      JSON.stringify(value),
    );
  }

  async get(key) {
    const value = await window.top.flutter_inappwebview.callHandler(
      'authentication_store_get',
      `${this.type}_${key}`,
    );
    try {
      return value ? JSON.parse(value) : undefined;
    } catch (ex) {
      Logger.error(
        `[MobileUserManager::FlutterStateStore::get] - Failed getting electron auth store value ${ex.message}`,
      );
      return undefined;
    }
  }

  async remove(key) {
    await window.top.flutter_inappwebview.callHandler(
      'authentication_store_remove',
      `${this.type}_${key}`,
    );
  }

  async getAllKeys() {
    return window.top.flutter_inappwebview.callHandler(
      'authentication_store_getAllKeys',
      `${this.type}`,
    );
  }
}

const MobileUserManager = ({
  children,
  basePath,
  onSessionLoaded,
  oidcAuthority,
  silentRenewDisabled,
  silentRedirectUri,
  clientId,
  postLogoutRedirectUri,
  externalExpired,
}) => {
  const onMobileSessionLoaded = useCallback(
    async session => {
      if (session && session?.userType) {
        Logger.log(
          `[MobileUserManager::onMobileSessionLoaded] - setting mobile user type to ${session.userType}`,
        );
        await window.top.flutter_inappwebview.callHandler(
          'authentication_setUserType',
          session.userType,
        );
      } else {
        Logger.log(`[MobileUserManager::onMobileSessionLoaded] - resetting mobile user type`);
        await window.top.flutter_inappwebview.callHandler('authentication_setUserType', null);
      }
      onSessionLoaded(session);
    },
    [onSessionLoaded],
  );

  const onLogout = useCallback(async () => {
    Logger.log(`[MobileUserManager::onLogout] - resetting mobile user type`);
    return window.top.flutter_inappwebview.callHandler('authentication_logout');
  }, []);

  return (
    <GenericUserManager
      basePath={basePath}
      silentRedirectUri={silentRedirectUri}
      oidcAuthority={oidcAuthority}
      clientId={clientId}
      userManagerType={userManagerTypes.MobileApp}
      extraScopes={['offline_access']}
      postLogoutRedirectUri={postLogoutRedirectUri}
      onSessionLoaded={onMobileSessionLoaded}
      onLogout={onLogout}
      silentRenewDisabled={silentRenewDisabled}
      externalExpired={externalExpired}
      getWebstorageStateStore={() => new FlutterStateStore({ type: 'oidc_session' })}
    >
      {children}
    </GenericUserManager>
  );
};

// ######################################################################################
// ## PropTypes
// ######################################################################################

MobileUserManager.propTypes = {
  children: PropTypes.node.isRequired,
  basePath: PropTypes.string.isRequired,
  onSessionLoaded: PropTypes.func,
  silentRenewDisabled: PropTypes.bool,
  externalExpired: PropTypes.bool,
  oidcAuthority: PropTypes.string.isRequired,
  clientId: PropTypes.oneOf(Object.values(oidcClientIds)).isRequired,
  postLogoutRedirectUri: PropTypes.string.isRequired,
  silentRedirectUri: PropTypes.string.isRequired,
};

MobileUserManager.defaultProps = {
  onSessionLoaded: () => {},
  silentRenewDisabled: false,
  externalExpired: false,
};

export default MobileUserManager;
